/**
 * Scene 2 — "Talk to Your Cell". Live Voice-AI roundtrip im Browser.
 *
 * UX:
 *   * Hold-to-Talk-Button links (PTT-Metapher fuer Funk-Vertraute)
 *   * Conversation-Liste in der Mitte (User-Transkript + AMZEL-Antwort)
 *   * Live-Pipeline-Diagramm rechts: Mic -> STT -> AMZEL Brain -> TTS
 *     hebt die gerade aktive Stage hervor
 *
 * Tech:
 *   * Web Speech API fuer STT + TTS (kein Backend-Roundtrip)
 *   * Pattern-Bank in scene2-data.ts fuer reproduzierbare Demo-Antworten
 *
 * Generische Site-Labels (Site Alpha) — keine realen Site-Codes.
 */
import { useCallback, useState } from "react";
import {
  Brain,
  CheckCircle2,
  ChevronRight,
  Cpu,
  Lightbulb,
  Mic,
  MicOff,
  RadioTower,
  Volume2,
} from "lucide-react";

import { SCENE2_PATTERNS, answerForTranscript } from "./scene2-data";
import { useSpeechRecognition, useSpeechSynthesis } from "./use-voice";

interface ConversationTurn {
  id: string;
  question: string;
  answer: string;
  timestamp: number;
}

type PipelineStage = "idle" | "listening" | "thinking" | "speaking";

export function SceneTalkToCell() {
  const [conversation, setConversation] = useState<ConversationTurn[]>([]);
  const [stage, setStage] = useState<PipelineStage>("idle");

  const tts = useSpeechSynthesis("en-US");

  const handleFinalTranscript = useCallback(
    (transcript: string) => {
      setStage("thinking");
      // Tiny artificial think-time so the user sees the brain "process"
      window.setTimeout(() => {
        const answer = answerForTranscript(transcript);
        const turn: ConversationTurn = {
          id: `${transcript.slice(0, 20)}-${conversation.length}`,
          question: transcript,
          answer,
          timestamp: Date.now(),
        };
        setConversation((prev) => [...prev, turn]);
        setStage("speaking");
        tts.speak(answer);
        // After speaking ends, back to idle (we approximate by answer length)
        const estimatedMs = Math.max(2000, answer.length * 60);
        window.setTimeout(() => setStage("idle"), estimatedMs);
      }, 700);
    },
    [conversation.length, tts],
  );

  const stt = useSpeechRecognition(handleFinalTranscript, "en-US");

  const handlePttDown = useCallback(() => {
    if (!stt.isSupported) return;
    setStage("listening");
    stt.start();
  }, [stt]);

  const handlePttUp = useCallback(() => {
    if (!stt.isSupported) return;
    stt.stop();
  }, [stt]);

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Left: PTT button */}
      <section className="lg:col-span-1">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
            Push to Talk
          </h2>
          <PttButton
            onDown={handlePttDown}
            onUp={handlePttUp}
            isSupported={stt.isSupported}
            isListening={stt.isListening}
          />
          <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
            {stt.isListening
              ? "Listening… release to send."
              : "Hold the button and ask a question."}
          </p>
          {stt.transcript && (
            <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
                Live transcript
              </div>
              <div className="mt-1 italic">"{stt.transcript}"</div>
            </div>
          )}

          {!stt.isSupported && (
            <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
              Your browser doesn't expose the Web Speech API. Use Chrome,
              Edge, or Safari on desktop for the live demo.
            </p>
          )}

          <SamplePrompts />
        </div>
      </section>

      {/* Middle: Conversation */}
      <section className="lg:col-span-1">
        <Conversation turns={conversation} stage={stage} />
      </section>

      {/* Right: Pipeline */}
      <section className="lg:col-span-1">
        <Pipeline stage={stage} />
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PTT Button
// ---------------------------------------------------------------------------

function PttButton({
  onDown,
  onUp,
  isSupported,
  isListening,
}: Readonly<{
  onDown: () => void;
  onUp: () => void;
  isSupported: boolean;
  isListening: boolean;
}>) {
  return (
    <button
      type="button"
      onMouseDown={onDown}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchStart={(e) => { e.preventDefault(); onDown(); }}
      onTouchEnd={(e) => { e.preventDefault(); onUp(); }}
      disabled={!isSupported}
      className={`mt-4 group relative grid h-44 w-full place-items-center rounded-3xl text-white transition-all select-none ${
        isListening
          ? "bg-rose-600 ring-4 ring-rose-300 dark:ring-rose-500/40 shadow-xl shadow-rose-500/30"
          : "bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {isListening ? (
        <>
          <Mic className="size-16" aria-hidden />
          <span className="mt-2 text-xs font-semibold uppercase tracking-widest">
            Listening…
          </span>
          <span className="absolute inset-0 animate-ping rounded-3xl bg-rose-400 opacity-25" aria-hidden />
        </>
      ) : (
        <>
          <Mic className="size-16" aria-hidden />
          <span className="mt-2 text-xs font-semibold uppercase tracking-widest">
            Hold to Talk
          </span>
        </>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Sample prompts
// ---------------------------------------------------------------------------

function SamplePrompts() {
  return (
    <div className="mt-6">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        <Lightbulb className="size-3.5" aria-hidden />
        Try asking
      </div>
      <ul className="space-y-1.5">
        {SCENE2_PATTERNS.slice(0, 6).map((p) => (
          <li
            key={p.hint}
            className="rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600 dark:bg-slate-800/50 dark:text-slate-300"
          >
            "{p.hint}"
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Conversation
// ---------------------------------------------------------------------------

function Conversation({
  turns,
  stage,
}: Readonly<{ turns: ConversationTurn[]; stage: PipelineStage }>) {
  return (
    <div className="h-full rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        Conversation
      </h2>
      {turns.length === 0 ? (
        <div className="mt-8 grid place-items-center rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 p-12 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
          <Mic className="size-8 text-slate-300 dark:text-slate-600" aria-hidden />
          <p className="mt-3">Hold the microphone and ask AMZEL a question.</p>
        </div>
      ) : (
        <ol className="mt-4 space-y-4">
          {turns.map((t) => (
            <li key={t.id} className="space-y-2">
              <div className="ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-slate-900 px-4 py-2.5 text-sm text-white dark:bg-slate-100 dark:text-slate-900">
                {t.question}
              </div>
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-emerald-50 px-4 py-2.5 text-sm text-emerald-900 dark:bg-emerald-500/15 dark:text-emerald-100">
                <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
                  <RadioTower className="size-3" aria-hidden />
                  AMZEL
                </div>
                {t.answer}
              </div>
            </li>
          ))}
          {stage === "thinking" && (
            <li className="max-w-[85%] rounded-2xl rounded-tl-sm bg-slate-100 px-4 py-2.5 text-sm italic text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              <span className="inline-block animate-pulse">AMZEL is thinking…</span>
            </li>
          )}
        </ol>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pipeline diagram
// ---------------------------------------------------------------------------

function Pipeline({ stage }: Readonly<{ stage: PipelineStage }>) {
  const stages: ReadonlyArray<{
    id: PipelineStage;
    label: string;
    sub: string;
    icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
    color: string;
  }> = [
    { id: "listening", label: "Microphone", sub: "Web Audio API · 16 kHz",
      icon: Mic, color: "text-rose-600 bg-rose-50 border-rose-200 dark:bg-rose-500/10 dark:border-rose-500/30" },
    { id: "listening", label: "Speech-to-Text", sub: "Browser STT · prod = AWS Transcribe",
      icon: Volume2, color: "text-sky-600 bg-sky-50 border-sky-200 dark:bg-sky-500/10 dark:border-sky-500/30" },
    { id: "thinking", label: "AMZEL Brain", sub: "Bedrock Claude · context injected",
      icon: Brain, color: "text-violet-600 bg-violet-50 border-violet-200 dark:bg-violet-500/10 dark:border-violet-500/30" },
    { id: "speaking", label: "Text-to-Speech", sub: "Browser TTS · prod = Polly",
      icon: MicOff, color: "text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30" },
    { id: "speaking", label: "Radio Out", sub: "DMR TMS · Vocoded AMBE+2",
      icon: RadioTower, color: "text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30" },
  ];

  return (
    <div className="h-full rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        <Cpu className="size-4" aria-hidden />
        Live Pipeline
      </h2>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        Voice in · AI · Voice out
      </p>

      <ol className="mt-5 space-y-3">
        {stages.map((s, idx) => {
          const isActive = stage === s.id;
          const Icon = s.icon;
          return (
            <li key={s.label} className="flex items-center gap-3">
              <div
                className={`grid size-10 shrink-0 place-items-center rounded-xl border transition-all ${
                  isActive
                    ? `${s.color} scale-110 shadow-md`
                    : "border-slate-200 bg-slate-50 text-slate-400 dark:border-slate-800 dark:bg-slate-800/50 dark:text-slate-500"
                }`}
              >
                <Icon className="size-5" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className={`text-sm font-semibold ${
                    isActive ? "text-slate-900 dark:text-slate-100" : "text-slate-500 dark:text-slate-400"
                  }`}
                >
                  {s.label}
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400">{s.sub}</div>
              </div>
              {idx < stages.length - 1 && (
                <ChevronRight className="size-4 shrink-0 text-slate-300 dark:text-slate-700" aria-hidden />
              )}
            </li>
          );
        })}
      </ol>

      <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100">
        <div className="flex items-center gap-1.5 font-semibold">
          <CheckCircle2 className="size-3.5" aria-hidden />
          Edge-first
        </div>
        <p className="mt-1 leading-relaxed">
          Audio never leaves the site. Only aggregates and on-demand transcripts
          flow to AMZEL Cloud — GDPR-clean by architecture, not by policy.
        </p>
      </div>
    </div>
  );
}
